/**
 * Cross-chunk runtime registry.
 *
 * CDN bootstrap and rspack app bundles may evaluate separate copies of this
 * module. Slot state is pinned on globalThis so register/get still share one
 * bag until consumers import the same CDN URL as an rspack external.
 */

export const RUNTIME_REGISTRY_GLOBAL_KEY = '__CMX_RUNTIME_REGISTRY__' as const;

/**
 * Minimal registry contract — any non-primitive object of methods/values
 * (plain object or class instance, e.g. Datadog's RUM SDK, which has no
 * index signature). Consumers narrow to their real shape (e.g. `CmxRumApi`,
 * the logging npm surface) via the generic on
 * `getRegisteredRum`/`getRegisteredLogging`; the registry itself stays free
 * of any dependency on those packages' types.
 */
export type RegistrySlotValue = object;

export type RuntimeRegistrySlots = {
  rum: RegistrySlotValue | undefined;
  logging: RegistrySlotValue | undefined;
};

type GlobalWithRegistry = typeof globalThis & {
  [RUNTIME_REGISTRY_GLOBAL_KEY]?: RuntimeRegistrySlots;
};

function getSlots(): RuntimeRegistrySlots {
  const g = globalThis as GlobalWithRegistry;
  if (!g[RUNTIME_REGISTRY_GLOBAL_KEY]) {
    g[RUNTIME_REGISTRY_GLOBAL_KEY] = { rum: undefined, logging: undefined };
  }
  return g[RUNTIME_REGISTRY_GLOBAL_KEY];
}

export function registerRum(instance: RegistrySlotValue): void {
  getSlots().rum = instance;
}

export function getRegisteredRum<T extends RegistrySlotValue = RegistrySlotValue>(): T | undefined {
  return getSlots().rum as T | undefined;
}

export function registerLogging(impl: RegistrySlotValue): void {
  getSlots().logging = impl;
}

export function getRegisteredLogging<T extends RegistrySlotValue = RegistrySlotValue>(): T | undefined {
  return getSlots().logging as T | undefined;
}

/** Test helper — clears slots without removing the global bag. */
export function resetRuntimeRegistryForTests(): void {
  const slots = getSlots();
  slots.rum = undefined;
  slots.logging = undefined;
}
